import React, { useState, useEffect, useCallback, useMemo } from 'react';
import type { Account, AccountsResponse, PortalSwitchAccountProps } from '../../types/switch-account';
import { Button } from 'react-aria-components';
import BaseModal from '../components/ui/BaseModal';
import SvgIcon from '../utils/SvgIcon';
import ContextMenu from '../components/ui/ContextMenu';

const PortalSwitchAccount = ({ isOpen, onOpenChange, labels }: PortalSwitchAccountProps) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [currentAccountId, setCurrentAccountId] = useState<string>('');
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSwitching, setIsSwitching] = useState<boolean>(false);
  const [hasOverflow, setHasOverflow] = useState<boolean>(false);
  const [isDesktop, setIsDesktop] = useState<boolean>(globalThis.innerWidth >= 1024);
  const listRef = React.useRef<HTMLDivElement>(null);

  // Fetch accounts when modal opens
  useEffect(() => {
    if (isOpen && accounts.length === 0) {
      fetchAccounts();
    }
  }, [isOpen, accounts.length]);

  const fetchAccounts = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/portal/account/get-accounts/');
      if (!response.ok) throw new Error('Failed to fetch accounts');

      const data: AccountsResponse = await response.json();
      // Handle nested accounts structure
      const accountsList = data.accounts?.accounts || [];
      setAccounts(accountsList);

      if (data.currentAccount?.selectedAccount) {
        setCurrentAccountId(data.currentAccount.selectedAccount.accountId);
        setSelectedAccountId(data.currentAccount.selectedAccount.accountId);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchAccount = async () => {
    if (!selectedAccountId || selectedAccountId === currentAccountId) return;

    setIsSwitching(true);
    try {
      const response = await fetch('/portal/account/switch/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(selectedAccountId),
      });

      if (!response.ok) throw new Error('Failed to switch account');

      // Redirect to dashboard
      globalThis.location.href = '/';
    } catch (error) {
      console.error('Error switching account:', error);
      setIsSwitching(false);
    }
  };

  const handleSetDefault = async (accountId: string) => {
    try {
      const response = await fetch('/portal/account/set-default/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(accountId),
      });

      if (!response.ok) throw new Error('Failed to set default account');

      // Update local state
      setAccounts(prev =>
        prev.map(acc => ({
          ...acc,
          isDefault: acc.accountId === accountId,
        }))
      );
    } catch (error) {
      console.error('Error setting default account:', error);
    }
  };

  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) return accounts;

    const query = searchQuery.toLowerCase();
    return accounts.filter(
      acc => acc.accountName.toLowerCase().includes(query) || acc.arAccountCode.toLowerCase().includes(query)
    );
  }, [accounts, searchQuery]);

  // Check for overflow when filtered accounts change
  useEffect(() => {
    const checkOverflow = () => {
      if (listRef.current) {
        const hasScroll = listRef.current.scrollHeight > listRef.current.clientHeight;
        setHasOverflow(hasScroll);
      }
    };

    checkOverflow();

    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        checkOverflow();
        setIsDesktop(globalThis.innerWidth >= 1024);
      }, 150);
    };

    globalThis.addEventListener('resize', handleResize);
    return () => {
      globalThis.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [filteredAccounts, isLoading]);

  const handleCloseModal = useCallback(() => {
    onOpenChange(false);
    // Reset search when closing
    setSearchQuery('');
    setSelectedAccountId(currentAccountId);
  }, [currentAccountId, onOpenChange]);

  const canSwitchAccount = selectedAccountId && selectedAccountId !== currentAccountId;

  const getListContent = () => {
    if (isLoading) {
      return <div className="switch-account__loading">Loading accounts...</div>;
    }

    if (filteredAccounts.length === 0) {
      return (
        <div className="switch-account__empty">
          <p className="switch-account__empty-title">
            {searchQuery ? labels.NoResultsTitle || 'No results found' : 'No accounts available'}
          </p>
          {searchQuery && (
            <p className="switch-account__empty-message">
              {labels.NoResultsDescription || 'Please try amending your search term'}
            </p>
          )}
        </div>
      );
    }

    return filteredAccounts.map(account => {
      const isCurrentSelected = account.accountId === selectedAccountId && account.accountId === currentAccountId;
      return (
        <div
          key={account.accountId}
          className={`switch-account__item${
            account.accountId === currentAccountId ? ' switch-account__item--current' : ''
          }${account.accountId === selectedAccountId ? ' switch-account__item--selected' : ''}`}
        >
          <button
            type="button"
            className="switch-account__item-button"
            onClick={() => setSelectedAccountId(account.accountId)}
            aria-pressed={account.accountId === selectedAccountId}
            disabled={isCurrentSelected}
          >
            <div className="switch-account__item-radio">
              <span className="switch-account__item-radio-inner" />
            </div>
            <div className="switch-account__item-content">
              <div className="switch-account__item-name">
                {account.accountName}
                {account.isDefault && <span className="switch-account__item-badge">(Default)</span>}
              </div>
              <div className="switch-account__item-number">{account.arAccountCode}</div>
            </div>
          </button>
          {!account.isDefault && (
            <ContextMenu
              trigger={<SvgIcon iconName="ellipsis" />}
              triggerClassName="switch-account__item-menu"
              ariaLabel={`Options for ${account.accountName}`}
              placement={isDesktop ? 'bottom' : 'bottom right'}
              items={[
                {
                  id: 'set-default',
                  label: labels.SetAsDefault || 'Set as default',
                  onSelect: () => {
                    void handleSetDefault(account.accountId);
                  },
                },
              ]}
            />
          )}
        </div>
      );
    });
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={labels.ModalTitle || 'Switch company account'}
      modalClassName="modal--switch-account"
      dialogClassName="modal__content--switch-account"
      closeAriaLabel="Close switch account modal"
    >
      <div className="switch-account">
        {/* Search */}
        <div className="switch-account__search">
          <div className="switch-account__search-wrapper">
            <input
              name="switch-account-search"
              type="text"
              className="switch-account__search-input"
              placeholder={labels.SearchPlaceholder || 'Type to search'}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              aria-label="Search accounts"
            />
            <SvgIcon iconName="search" className="switch-account__search-icon" />
          </div>
        </div>

        {/* Account List */}
        <div className={`switch-account__list-wrapper${hasOverflow ? ' switch-account__list-wrapper--overflow' : ''}`}>
          <div className={`switch-account__list${hasOverflow ? ' switch-account__list--overflow' : ''}`} ref={listRef}>
            {getListContent()}
          </div>
        </div>

        {/* Actions */}
        <div className="switch-account__actions">
          <Button className="cta cta--secondary" onPress={handleCloseModal} isDisabled={isSwitching}>
            {labels.CancelCta || 'Cancel'}
          </Button>
          <Button
            className="cta cta--primary"
            onPress={handleSwitchAccount}
            isDisabled={!canSwitchAccount || isSwitching}
          >
            {isSwitching ? 'Switching...' : labels.SwitchCta || 'Switch account'}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};

export default PortalSwitchAccount;
